import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  GoogleAuthProvider,
  FacebookAuthProvider,
  GithubAuthProvider,
  signInWithPopup,
  getIdToken
} from 'firebase/auth';
import { auth } from '../Firebase';

const SocialRegisterButtons = () => {
  const navigate = useNavigate();
  const { register: authRegister } = useAuth();
  const [loading, setLoading] = useState(null); // Track which provider is loading
  const [errors, setErrors] = useState({});

  const handleSocialAuth = async (provider, providerName) => {
    setLoading(providerName);
    setErrors({});

    try {
      console.log(`Starting ${providerName} authentication...`);

      // Create the appropriate provider
      let authProvider;
      switch (provider) {
        case 'google':
          authProvider = new GoogleAuthProvider();
          authProvider.addScope('email');
          authProvider.addScope('profile');
          break;
        case 'facebook':
          authProvider = new FacebookAuthProvider();
          authProvider.addScope('email');
          authProvider.addScope('public_profile');
          break;
        case 'github':
          authProvider = new GithubAuthProvider();
          authProvider.addScope('user:email');
          break;
        default:
          throw new Error('Unsupported provider');
      }

      // Sign in with popup
      const result = await signInWithPopup(auth, authProvider);
      const user = result.user;

      console.log(`${providerName} authentication successful:`, user.uid);

      // Get Firebase ID token
      const idToken = await getIdToken(user);
      console.log(`${providerName} ID token obtained`);

      // Extract user info from the provider
      let firstName = '';
      let lastName = '';
      let email = user.email || '';

      // Parse display name or use provider data
      if (user.displayName) {
        const nameParts = user.displayName.split(' ');
        firstName = nameParts[0] || '';
        lastName = nameParts.slice(1).join(' ') || '';
      }

      // For some providers, we might get additional info
      if (result._tokenResponse) {
        const tokenResponse = result._tokenResponse;

        // Google provides firstName/lastName in profile
        if (provider === 'google' && tokenResponse.firstName) {
          firstName = tokenResponse.firstName;
          lastName = tokenResponse.lastName || '';
        }

        // Facebook provides name in profile
        if (provider === 'facebook' && tokenResponse.name) {
          const nameParts = tokenResponse.name.split(' ');
          firstName = nameParts[0] || '';
          lastName = nameParts.slice(1).join(' ') || '';
        }
      }

      // Register/Login with backend using Firebase ID token
      const userData = await authRegister({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.toLowerCase().trim(),
        provider: provider,
        idToken: idToken
      });

      console.log(`${providerName} registration/login successful:`, userData);

      // Navigate to profile or home
      navigate('/profile');

    } catch (error) {
      console.error(`${providerName} authentication error:`, error);

      // Handle specific Firebase Auth errors
      if (error.code === 'auth/popup-closed-by-user') {
        setErrors({ social: 'Authentication cancelled by user' });
      } else if (error.code === 'auth/popup-blocked') {
        setErrors({ social: 'Popup blocked by browser. Please allow popups and try again.' });
      } else if (error.code === 'auth/account-exists-with-different-credential') {
        setErrors({ social: 'An account already exists with this email using a different sign-in method.' });
      } else if (error.code === 'auth/network-request-failed') {
        setErrors({ social: 'Network error. Please check your connection and try again.' });
      } else {
        setErrors({ social: error.message || `Failed to authenticate with ${providerName}` });
      }
    } finally {
      setLoading(null);
    }
  };

  const buttonStyle = (provider, isLoading) => ({
    width: '100%',
    padding: '12px 20px',
    margin: '8px 0',
    border: 'none',
    borderRadius: '8px',
    cursor: isLoading ? 'not-allowed' : 'pointer',
    fontSize: '16px',
    fontWeight: '500',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    transition: 'all 0.3s ease',
    opacity: isLoading ? 0.7 : 1,
    backgroundColor: provider === 'google' ? '#4285f4' :
                     provider === 'facebook' ? '#1877f2' :
                     provider === 'github' ? '#333' : '#666',
    color: 'white'
  });

  return (
    <div style={{ marginTop: '20px' }}>
      <div style={{
        textAlign: 'center',
        marginBottom: '16px',
        color: '#666',
        fontSize: '14px'
      }}>
        Ou continuer avec
      </div>

      <button
        onClick={() => handleSocialAuth('google', 'Google')}
        disabled={loading !== null}
        style={buttonStyle('google', loading === 'Google')}
        onMouseEnter={(e) => {
          if (loading !== 'Google') {
            e.target.style.backgroundColor = '#3367d6';
          }
        }}
        onMouseLeave={(e) => {
          if (loading !== 'Google') {
            e.target.style.backgroundColor = '#4285f4';
          }
        }}
      >
        {loading === 'Google' ? (
          <>Chargement...</>
        ) : (
          <>
            <svg width="20" height="20" viewBox="0 0 24 24">
              <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continuer avec Google
          </>
        )}
      </button>

      <button
        onClick={() => handleSocialAuth('facebook', 'Facebook')}
        disabled={loading !== null}
        style={buttonStyle('facebook', loading === 'Facebook')}
        onMouseEnter={(e) => {
          if (loading !== 'Facebook') {
            e.target.style.backgroundColor = '#166fe5';
          }
        }}
        onMouseLeave={(e) => {
          if (loading !== 'Facebook') {
            e.target.style.backgroundColor = '#1877f2';
          }
        }}
      >
        {loading === 'Facebook' ? (
          <>Chargement...</>
        ) : (
          <>
            <svg width="20" height="20" viewBox="0 0 24 24">
              <path fill="currentColor" d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
            </svg>
            Continuer avec Facebook
          </>
        )}
      </button>

      <button
        onClick={() => handleSocialAuth('github', 'GitHub')}
        disabled={loading !== null}
        style={buttonStyle('github', loading === 'GitHub')}
        onMouseEnter={(e) => {
          if (loading !== 'GitHub') {
            e.target.style.backgroundColor = '#24292e';
          }
        }}
        onMouseLeave={(e) => {
          if (loading !== 'GitHub') {
            e.target.style.backgroundColor = '#333';
          }
        }}
      >
        {loading === 'GitHub' ? (
          <>Chargement...</>
        ) : (
          <>
            <svg width="20" height="20" viewBox="0 0 24 24">
              <path fill="currentColor" d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
            </svg>
            Continuer avec GitHub
          </>
        )}
      </button>

      {errors.social && (
        <div style={{
          color: '#e74c3c',
          fontSize: '14px',
          marginTop: '10px',
          textAlign: 'center',
          padding: '8px',
          backgroundColor: '#fdf2f2',
          borderRadius: '4px',
          border: '1px solid #f5c6cb'
        }}>
          {errors.social}
        </div>
      )}

      <div style={{
        textAlign: 'center',
        marginTop: '16px',
        color: '#888',
        fontSize: '12px'
      }}>
        En continuant, vous acceptez nos{' '}
        <a href="/terms" style={{ color: '#4285f4', textDecoration: 'none' }}>
          Conditions d'utilisation
        </a>
        {' '}et notre{' '}
        <a href="/privacy" style={{ color: '#4285f4', textDecoration: 'none' }}>
          Politique de confidentialité
        </a>
      </div>
    </div>
  );
};

export default SocialRegisterButtons;
